import { useLayoutEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { notesApi, tasksApi, commentsApi, emailsApi, type Note, type Comment, type Task } from '../api/activities'
import { usersApi } from '../api/settings'
import { useAuthStore } from '../store/auth'
import { MentionTextarea } from './MentionTextarea'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { cn, formatDateTime, fullName } from '../lib/utils'
import { DocumentsTab } from './DocumentsTab'
import { WhatsAppLeadTab } from '../pages/whatsapp/WhatsAppLeadTab'
import { ProformasTab } from './ProformasTab'
import { Trash2, Send, MessageSquare, StickyNote, Mail, CheckSquare, Check, Clock, Paperclip, MessageCircle, Receipt } from 'lucide-react'
import toast from 'react-hot-toast'

type Tab = 'comment' | 'note' | 'whatsapp' | 'email' | 'task' | 'documents' | 'proformas'

interface Props {
  companyId: string
  refType: string
  refId: string
}

type UserShape = { id: string; firstName: string; lastName: string }

const ALL_TAB_META: { id: Tab; label: string; icon: React.ElementType; refTypes?: string[] }[] = [
  { id: 'note',    label: 'Note',    icon: StickyNote },
  { id: 'comment', label: 'Comment', icon: MessageSquare },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, refTypes: ['leads'] },
  { id: 'email',   label: 'Email',   icon: Mail },
  { id: 'task',      label: 'Task',      icon: CheckSquare },
  { id: 'documents', label: 'Documents', icon: Paperclip, refTypes: ['leads'] },
  { id: 'proformas', label: 'Proformas', icon: Receipt },
]

// Deterministic avatar background based on user ID
const AVATAR_COLORS = [
  'bg-blue-500', 'bg-violet-500', 'bg-rose-500', 'bg-amber-500',
  'bg-teal-500', 'bg-cyan-500', 'bg-fuchsia-500', 'bg-indigo-500',
]
function avatarColor(id: string) {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

function Avatar({ user, size = 'sm' }: { user: UserShape; size?: 'sm' | 'md' }) {
  const initials = `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase()
  return (
    <div className={cn(
      'rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0',
      avatarColor(user.id),
      size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-8 h-8 text-xs',
    )}>
      {initials}
    </div>
  )
}

function UnknownAvatar() {
  return (
    <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
      <span className="text-[10px] font-semibold text-gray-600">?</span>
    </div>
  )
}

export function ComposeArea({ companyId, refType, refId }: Props) {
  const [tab, setTab] = useState<Tab>('note')
  const qc = useQueryClient()
  const refetchActivities = () => qc.invalidateQueries({ queryKey: ['activities', companyId, refType, refId] })
  const refetchComments   = () => qc.invalidateQueries({ queryKey: ['comments',   companyId, refType, refId] })
  const refetchNotes      = () => qc.invalidateQueries({ queryKey: ['notes',      companyId, refType, refId] })

  const { data: users } = useQuery({
    queryKey: ['users', companyId],
    queryFn: () => usersApi.list(companyId).then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const TAB_META = ALL_TAB_META.filter(t => !t.refTypes || t.refTypes.includes(refType))

  return (
    <div className="border border-gray-200 rounded-xl overflow-visible bg-white shadow-sm relative">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        {TAB_META.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors',
              tab === id
                ? 'bg-white text-primary-700 border-b-2 border-primary-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {tab === 'comment' && (
          <CommentTab
            companyId={companyId} refType={refType} refId={refId}
            users={users ?? []}
            onSuccess={() => { refetchActivities(); refetchComments() }}
          />
        )}
        {tab === 'note' && (
          <NoteTab
            companyId={companyId} refType={refType} refId={refId}
            users={users ?? []}
            onSuccess={() => { refetchActivities(); refetchNotes() }}
          />
        )}
        {tab === 'whatsapp' && refType === 'leads' && (
          <WhatsAppLeadTab companyId={companyId} leadId={refId} />
        )}
        {tab === 'email' && (
          <EmailForm companyId={companyId} refType={refType} refId={refId} onSuccess={refetchActivities} />
        )}
        {tab === 'task' && (
          <TasksSection
            companyId={companyId} refType={refType} refId={refId}
            users={users ?? []}
            onSuccess={refetchActivities}
          />
        )}
        {tab === 'documents' && refType === 'leads' && (
          <DocumentsTab companyId={companyId} leadId={refId} />
        )}
        {tab === 'proformas' && (<ProformasTab companyId={companyId} refType={refType as 'leads' | 'deals'} refId={refId} />)}
      </div>
    </div>
  )
}

// ── Comment tab ───────────────────────────────────────────────────────────────

function CommentTab({ companyId, refType, refId, users, onSuccess }: Props & {
  users: UserShape[]
  onSuccess: () => void
}) {
  const qc = useQueryClient()
  const [content, setContent] = useState('')
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const { user: me } = useAuthStore()

  const { data: comments } = useQuery({
    queryKey: ['comments', companyId, refType, refId],
    queryFn: () => commentsApi.list(companyId, refType, refId).then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => commentsApi.remove(companyId, refType, refId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comments', companyId, refType, refId] }),
  })

  const addMutation = useMutation({
    mutationFn: () => commentsApi.create(companyId, refType, refId, { content }),
    onSuccess: () => { setContent(''); onSuccess(); toast.success('Comment added') },
  })

  const resolveUser = (id: string): UserShape | undefined => users.find(u => u.id === id)

  const renderContent = (text: string) =>
    text.replace(/@\{([0-9a-f-]{36})\}/gi, (_, uid) => {
      const u = resolveUser(uid)
      return u ? `@${fullName(u.firstName, u.lastName)}` : '@unknown'
    })

  return (
    <div>
      <ConfirmDialog
        isOpen={!!pendingDeleteId}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={() => deleteMutation.mutate(pendingDeleteId!)}
        title="Delete comment?"
        message="This comment will be permanently removed."
        confirmLabel="Delete"
      />

      {comments && comments.length > 0 && (
        <div>
          <div className="px-4 pt-3 pb-1">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
              Comments ({comments.length})
            </span>
          </div>

          <div className="divide-y divide-gray-100">
            {comments.map((c: Comment) => {
              const author = resolveUser(c.createdBy)
              const isMine = me?.id === c.createdBy

              return (
                <div
                  key={c.id}
                  className={cn(
                    'px-4 py-3 group transition-colors flex gap-3',
                    isMine ? 'hover:bg-green-50/60' : 'hover:bg-blue-50/60',
                  )}
                >
                  {/* Accent bar */}
                  <div className={cn(
                    'w-0.5 rounded-full flex-shrink-0 self-stretch',
                    isMine ? 'bg-green-400' : 'bg-blue-400',
                  )} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        {author ? <Avatar user={author} size="sm" /> : <UnknownAvatar />}
                        <span className="text-xs font-semibold text-gray-800">
                          {author ? fullName(author.firstName, author.lastName) : 'User'}
                        </span>
                        <span className="text-gray-300 text-xs">·</span>
                        <span className={cn('text-xs', isMine ? 'text-green-600' : 'text-blue-600')}>
                          {formatDateTime(c.createdAt)}
                        </span>
                      </div>
                      <button
                        onClick={() => setPendingDeleteId(c.id)}
                        title="Delete comment"
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-gray-300 hover:text-red-500 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed pl-0.5">
                      {renderContent(c.content)}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="border-t border-gray-100" />
        </div>
      )}

      {/* Compose */}
      <div className="p-4 space-y-2">
        <MentionTextarea
          value={content}
          onChange={setContent}
          placeholder="Add a comment... type @ to mention a user"
          rows={3}
          users={users}
        />
        <div className="flex justify-end">
          <button
            onClick={() => addMutation.mutate()}
            disabled={!content.trim() || addMutation.isPending}
            className="btn-primary btn-sm btn"
          >
            <Send className="w-3 h-3" /> Comment
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Note tab ──────────────────────────────────────────────────────────────────

function NoteTab({ companyId, refType, refId, users, onSuccess }: Props & {
  users: UserShape[]
  onSuccess: () => void
}) {
  const qc = useQueryClient()
  const [content, setContent] = useState('')
  const boxRef = useRef<HTMLTextAreaElement>(null)

  // The composer grows with what is typed — 15 lines stay visible instead of
  // scrolling inside 4 rows — then caps and scrolls. Floor and ceiling are the
  // min/max-height on the textarea below; shrinking back needs the 'auto' reset.
  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [content])

  const { data: notes } = useQuery({
    queryKey: ['notes', companyId, refType, refId],
    queryFn: () => notesApi.list(companyId, refType, refId).then(r => r.data),
  })

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (id: string) => notesApi.remove(companyId, refType, refId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', companyId, refType, refId] }),
  })

  const addMutation = useMutation({
    mutationFn: () => notesApi.create(companyId, refType, refId, { content }),
    onSuccess: () => { setContent(''); onSuccess(); toast.success('Note saved') },
  })

  const resolveUser = (id: string): UserShape | undefined => users.find(u => u.id === id)

  return (
    <div>
      <ConfirmDialog
        isOpen={!!pendingDeleteId}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={() => deleteMutation.mutate(pendingDeleteId!)}
        title="Delete note?"
        message="This note will be permanently removed."
        confirmLabel="Delete"
      />

      {notes && notes.length > 0 && (
        <div>
          <div className="px-4 pt-3 pb-1">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
              Notes ({notes.length})
            </span>
          </div>

          <div className="divide-y divide-amber-100">
            {notes.map((n: Note) => {
              const author = resolveUser(n.createdBy)
              return (
                <div key={n.id} className="px-4 py-3 group hover:bg-amber-50/60 transition-colors flex gap-3">
                  {/* Yellow accent bar */}
                  <div className="w-0.5 bg-amber-400 rounded-full flex-shrink-0 self-stretch" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        {author ? <Avatar user={author} size="sm" /> : <UnknownAvatar />}
                        <span className="text-xs font-semibold text-gray-700">
                          {author ? fullName(author.firstName, author.lastName) : 'User'}
                        </span>
                        <span className="text-gray-300 text-xs">·</span>
                        <span className="text-xs text-amber-600">{formatDateTime(n.createdAt)}</span>
                      </div>
                      <button
                        onClick={() => setPendingDeleteId(n.id)}
                        title="Delete note"
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-gray-300 hover:text-red-500 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed pl-0.5">
                      {n.content}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="border-t border-gray-100" />
        </div>
      )}

      {/* Compose */}
      <div className="p-4 space-y-2">
        <textarea
          ref={boxRef}
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Write a note..."
          className="input resize-none overflow-y-auto"
          style={{ minHeight: '6rem', maxHeight: '26rem' }}
        />
        <div className="flex justify-end">
          <button
            onClick={() => addMutation.mutate()}
            disabled={!content.trim() || addMutation.isPending}
            className="btn-primary btn-sm btn"
          >
            Save Note
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Email form ────────────────────────────────────────────────────────────────

interface EmailFormFields { to: string; subject: string; body: string }

function EmailForm({ companyId, refType, refId, onSuccess }: Props & { onSuccess: () => void }) {
  const { register, handleSubmit, reset } = useForm<EmailFormFields>()
  const [files, setFiles] = useState<File[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const mutation = useMutation({
    mutationFn: (data: EmailFormFields) => emailsApi.send(companyId, refType, refId, {
      toAddresses: [data.to],
      subject: data.subject,
      bodyText: data.body,
    } as Record<string, unknown>),
    onSuccess: () => { reset(); setFiles([]); onSuccess(); toast.success('Email sent') },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to send email')
    },
  })

  return (
    <div className="p-3 space-y-2">
      <input {...register('to', { required: true })} placeholder="To email" className="input" type="email" />
      <input {...register('subject', { required: true })} placeholder="Subject" className="input" />
      <textarea {...register('body', { required: true })} placeholder="Message" rows={4} className="input resize-none" />

      {files.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {files.map((f, i) => (
            <span key={i} className="flex items-center gap-1 text-xs bg-gray-100 rounded px-2 py-0.5 text-gray-600">
              📎 {f.name}
              <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">×</button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button type="button" onClick={() => handleSubmit(d => mutation.mutate(d))()} disabled={mutation.isPending} className="btn btn-primary btn-sm">
          <Send className="w-3 h-3" /> {mutation.isPending ? 'Sending…' : 'Send Email'}
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} className="btn btn-ghost btn-sm p-1.5" title="Attach files">
          <Paperclip className="w-4 h-4 text-gray-500" />
        </button>
        <input ref={fileRef} type="file" multiple className="hidden"
          onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files ?? [])])} />
      </div>
    </div>
  )
}

// ── Task form ─────────────────────────────────────────────────────────────────

interface TaskFormFields { title: string; type: 'meeting' | 'reminder'; when?: string; description?: string }

function TaskForm({ companyId, refType, refId, onSuccess }: Props & { onSuccess: () => void }) {
  const { register, handleSubmit, reset, watch, setValue } = useForm<TaskFormFields>({
    defaultValues: { title: '', type: 'reminder', when: '', description: '' },
  })
  const taskType = watch('type')

  const mutation = useMutation({
    mutationFn: (data: TaskFormFields) => {
      const hasTime = !!data.when
      return tasksApi.create(companyId, refType, refId, {
        title: data.title,
        type: data.type,
        description: data.description || undefined,
        isAllDay: !hasTime,
        startAt: hasTime ? new Date(data.when as string).toISOString() : undefined,
      })
    },
    onSuccess: () => { reset(); onSuccess(); toast.success('Task created') },
  })

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="p-4 space-y-2">
      {/* Type selector */}
      <div className="flex gap-2">
        {(['meeting', 'reminder'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setValue('type', t)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
              taskType === t
                ? t === 'meeting'
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-amber-500 border-amber-500 text-white'
                : 'border-gray-300 text-gray-600 hover:border-gray-400',
            )}
          >
            {t === 'meeting' ? '📅 Meeting' : '🔔 Reminder'}
          </button>
        ))}
      </div>

      <input {...register('title', { required: true })} placeholder="Task title" className="input" />

      <div>
        <label className="form-label">Date &amp; time (optional)</label>
        <input type="datetime-local" {...register('when')} className="input" />
      </div>

      <textarea {...register('description')} rows={2} placeholder="Description (optional)" className="input resize-none" />
      <div className="flex justify-end">
        <button type="submit" disabled={mutation.isPending} className="btn-primary btn-sm btn">
          Create Task
        </button>
      </div>
    </form>
  )
}

// ── Tasks section (form + list) ───────────────────────────────────────────────

const MEETING_COLOR = '#6366f1'
const REMINDER_COLOR = '#f59e0b'

function TasksSection({ companyId, refType, refId, users, onSuccess }: Props & { users: UserShape[]; onSuccess: () => void }) {
  const qc = useQueryClient()
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const refetchTasks = () => qc.invalidateQueries({ queryKey: ['tasks', companyId, refType, refId] })

  const { data: tasks = [] } = useQuery({
    queryKey: ['tasks', companyId, refType, refId],
    queryFn: () => tasksApi.list(companyId, refType, refId).then(r => r.data),
    enabled: !!companyId && !!refId,
  })

  const completeMut = useMutation({
    mutationFn: (taskId: string) => tasksApi.update(companyId, refType, refId, taskId, { status: 'completed' }),
    onSuccess: () => { refetchTasks(); onSuccess() },
  })

  const removeMut = useMutation({
    mutationFn: (taskId: string) => tasksApi.remove(companyId, refType, refId, taskId),
    onSuccess: () => { refetchTasks(); onSuccess() },
  })

  const userMap = new Map(users.map(u => [u.id, u]))

  return (
    <div>
      <TaskForm companyId={companyId} refType={refType} refId={refId} onSuccess={() => { refetchTasks(); onSuccess() }} />

      {tasks.length > 0 && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {(tasks as Task[]).map(task => {
            const color = task.type === 'meeting' ? MEETING_COLOR : REMINDER_COLOR
            const assignedUser = task.assignedTo ? userMap.get(task.assignedTo) : undefined
            const isDone = task.status === 'completed' || task.status === 'cancelled'
            const timeStr = task.isAllDay
              ? (task.dueDate ? new Date(task.dueDate).toLocaleDateString([], { dateStyle: 'medium' }) : '')
              : task.startAt
                ? new Date(task.startAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
                : (task.dueDate ? new Date(task.dueDate).toLocaleDateString([], { dateStyle: 'medium' }) : '')
            return (
              <div key={task.id} className={cn('flex items-start gap-2.5 px-4 py-2.5', isDone && 'opacity-50')}>
                <button
                  onClick={() => !isDone && completeMut.mutate(task.id)}
                  disabled={isDone}
                  className={cn(
                    'mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors',
                    isDone ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-500',
                  )}
                >
                  {isDone && <Check className="w-2.5 h-2.5 text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-white flex-shrink-0"
                      style={{ backgroundColor: color }}
                    >
                      {task.type ?? 'reminder'}
                    </span>
                    <p className={cn('text-sm font-medium text-gray-800 truncate', isDone && 'line-through')}>{task.title}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {timeStr && (
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />{timeStr}
                      </span>
                    )}
                    {assignedUser && (
                      <span className="text-xs text-gray-400">· {fullName(assignedUser.firstName, assignedUser.lastName)}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setPendingDeleteId(task.id)}
                  className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!pendingDeleteId}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={() => { if (pendingDeleteId) removeMut.mutate(pendingDeleteId); setPendingDeleteId(null) }}
        title="Delete this task?"
        message="This task will be removed."
        confirmLabel="Delete"
        isPending={removeMut.isPending}
      />
    </div>
  )
}
